import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, errMsg } from '../lib/api';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const ST_STYLE = { active: 'bg-green-100 text-green-800', maintenance: 'bg-amber-100 text-amber-800', retired: 'bg-slate-100 text-slate-500' };
const fmt = (n) => Number(n || 0).toLocaleString();

export default function Assets() {
  const qc = useQueryClient();
  const [status, setStatus] = useState('all');
  const [q, setQ] = useState('');
  const { data: assets } = useQuery({ queryKey: ['assets'], queryFn: () => api.get('/assets').then((r) => r.data) });
  const [form, setForm] = useState({ tag: '', name: '', category: 'IT', location: '', cost: '', purchase_date: '', life_years: '5' });
  const inval = () => qc.invalidateQueries({ queryKey: ['assets'] });
  const createMut = useMutation({
    mutationFn: () => api.post('/assets', { ...form, cost: parseFloat(form.cost) || 0, life_years: parseInt(form.life_years, 10) || 5, purchase_date: form.purchase_date || null }),
    onSuccess: () => { toast.success('Asset registered'); setForm({ ...form, tag: '', name: '', cost: '' }); inval(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const patchMut = useMutation({
    mutationFn: ({ id, patch }) => api.patch(`/assets/${id}`, patch),
    onSuccess: () => { toast.success('Asset updated'); inval(); }, onError: (e) => toast.error(errMsg(e)),
  });
  const maintMut = useMutation({
    mutationFn: ({ id, note, cost }) => api.post(`/assets/${id}/maintenance`, { note, cost }),
    onSuccess: () => { toast.success('Maintenance logged'); inval(); }, onError: (e) => toast.error(errMsg(e)),
  });
  const retireMut = useMutation({
    mutationFn: (id) => api.delete(`/assets/${id}`),
    onSuccess: () => { toast.success('Asset retired'); inval(); }, onError: (e) => toast.error(errMsg(e)),
  });
  const doEdit = (a) => {
    const location = window.prompt('Location:', a.location || '') ?? a.location;
    const assigned_to = window.prompt('Assigned to:', a.assigned_to || '') ?? a.assigned_to;
    patchMut.mutate({ id: a.id, patch: { location, assigned_to, status: a.status === 'maintenance' ? 'active' : undefined } });
  };
  const doMaint = (a) => {
    const note = window.prompt(`Maintenance note for ${a.tag}:`); if (!note) return;
    const cost = parseFloat(window.prompt('Maintenance cost:', '0')) || 0;
    maintMut.mutate({ id: a.id, note, cost });
  };
  const filtered = (assets || []).filter((a) =>
    (status === 'all' || a.status === status) &&
    (!q || `${a.tag} ${a.name} ${a.category} ${a.location} ${a.assigned_to}`.toLowerCase().includes(q.toLowerCase())));
  const totals = filtered.reduce((acc, a) => ({ cost: acc.cost + (a.cost || 0), value: acc.value + (a.current_value || 0) }), { cost: 0, value: 0 });

  return (
    <div data-testid="assets-page">
      <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight mb-1">Asset Management</h1>
      <p className="text-sm text-slate-500 mb-6">Fixed-asset registry with straight-line depreciation, maintenance log & retirement</p>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <Input data-testid="assets-search-input" placeholder="Search tag, name, location..." value={q} onChange={(e) => setQ(e.target.value)} className="max-w-xs bg-white" />
        {['all', 'active', 'maintenance', 'retired'].map((s) => (
          <button key={s} data-testid={`assets-filter-${s}`} onClick={() => setStatus(s)}
            className={`text-xs rounded-full px-3 py-1.5 border capitalize ${status === s ? 'bg-slate-900 text-white border-slate-900' : 'border-slate-200 hover:border-slate-900'}`}>{s}</button>
        ))}
        <span className="ms-auto text-xs text-slate-500" data-testid="assets-totals">Book value <b className="text-slate-900">{fmt(totals.value)}</b> of {fmt(totals.cost)} purchased</span>
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-x-auto mb-6" data-testid="assets-table">
        <table className="w-full text-sm">
          <thead className="bg-slate-50"><tr className="text-left">
            {['Tag', 'Name', 'Category', 'Location / Assigned', 'Cost', 'Current value', 'Status', 'Actions'].map((h) => <th key={h} className="py-2 px-3 font-mono-label text-[10px] uppercase text-slate-400">{h}</th>)}
          </tr></thead>
          <tbody>
            {filtered.map((a, i) => (
              <tr key={a.id} className={`border-t border-slate-100 ${i % 2 ? 'bg-slate-50/50' : ''}`} data-testid={`asset-row-${a.id}`}>
                <td className="py-2 px-3 font-mono-label text-xs">{a.tag}</td>
                <td className="py-2 px-3 font-medium">{a.name}</td>
                <td className="py-2 px-3">{a.category}</td>
                <td className="py-2 px-3 text-xs">{a.location}{a.assigned_to ? ` - ${a.assigned_to}` : ''}</td>
                <td className="py-2 px-3">{fmt(a.cost)}</td>
                <td className="py-2 px-3 font-semibold">{fmt(a.current_value)}</td>
                <td className="py-2 px-3"><span className={`font-mono-label text-[9px] uppercase px-1.5 py-0.5 rounded ${ST_STYLE[a.status]}`}>{a.status}</span></td>
                <td className="py-2 px-3">
                  <div className="flex gap-2">
                    <button data-testid={`asset-edit-${a.id}`} onClick={() => doEdit(a)} className="text-xs underline text-orange-700">edit</button>
                    {a.status !== 'retired' && <button data-testid={`asset-maint-${a.id}`} onClick={() => doMaint(a)} className="text-xs underline text-amber-600">maintenance</button>}
                    {a.status !== 'retired' && <button data-testid={`asset-retire-${a.id}`} onClick={() => window.confirm(`Retire ${a.tag}?`) && retireMut.mutate(a.id)} className="text-xs underline text-slate-400">retire</button>}
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={8} className="p-6 text-center text-slate-400">No assets registered.</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-4 max-w-3xl" data-testid="asset-create-panel">
        <p className="font-mono-label text-[10px] uppercase text-slate-400 mb-2">Register asset</p>
        <div className="grid sm:grid-cols-4 gap-2">
          <Input data-testid="asset-tag-input" placeholder="Tag (LAP-001)" value={form.tag} onChange={(e) => setForm({ ...form, tag: e.target.value })} />
          <Input data-testid="asset-name-input" placeholder="Name (Dell Laptop)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <Input data-testid="asset-category-input" placeholder="Category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
          <Input data-testid="asset-location-input" placeholder="Location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
          <Input data-testid="asset-cost-input" type="number" placeholder="Cost" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} />
          <Input data-testid="asset-date-input" type="date" value={form.purchase_date} onChange={(e) => setForm({ ...form, purchase_date: e.target.value })} />
          <Input data-testid="asset-life-input" type="number" placeholder="Life (years)" value={form.life_years} onChange={(e) => setForm({ ...form, life_years: e.target.value })} />
          <Button data-testid="asset-create-button" className="bg-slate-900 hover:bg-slate-800" disabled={!form.tag || !form.name || createMut.isPending} onClick={() => createMut.mutate()}>Register</Button>
        </div>
      </div>
    </div>
  );
}
